/**
 * PayPeriodsPage (BON-246) — the pay calendar.
 *
 * Periods are contiguous by rule, so the "add" form pre-fills the only start
 * date the server will accept: the day after the calendar currently ends. The
 * admin picks an end date and a pay date; they never have to work out where
 * the next period begins.
 *
 * A period whose pay date has passed is shown but locked — editing it regroups
 * pay that has already been presented, so it needs a deliberate confirmation
 * (BON-247 will be the surface that presented it).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import {
  createPayPeriod,
  deletePayPeriod,
  fetchPayPeriods,
  nextStartAfter,
  type PayPeriodEntry,
} from '../../api/payPeriods';
import { usePermissions } from '../../hooks/usePermissions';
import { Permission } from '../../types/permissions';

const PERIODS_KEY = ['settings', 'payPeriods'];

/** The calendar's covered span, for the header line. */
export function coveredSpan(periods: PayPeriodEntry[]): string | null {
  if (periods.length === 0) return null;
  const first = periods.reduce((a, b) => (a.startsOnIso < b.startsOnIso ? a : b));
  const last = periods.reduce((a, b) => (a.endsOnIso > b.endsOnIso ? a : b));
  return `${first.startsOnIso} → ${last.endsOnIso}`;
}

function PeriodTable({
  periods,
  canEdit,
  busy,
  onRemove,
}: {
  periods: PayPeriodEntry[];
  canEdit: boolean;
  busy: boolean;
  onRemove: (id: string) => void;
}) {
  if (periods.length === 0) {
    return (
      <div style={{ fontSize: 12, color: 'var(--nb-subtle)' }}>
        Work dates outside the calendar have no pay date, so add the first period to start grouping
        pay.
      </div>
    );
  }

  return (
    <table className="nb-tbl">
      <thead>
        <tr>
          <th>Covered</th>
          <th>Paid on</th>
          <th>Status</th>
          {canEdit && <th></th>}
        </tr>
      </thead>
      <tbody>
        {periods.map((p) => (
          <tr key={p.id}>
            <td className="nb-mono">
              {p.startsOnIso} → {p.endsOnIso}
            </td>
            <td className="nb-mono">{p.payDateIso}</td>
            <td className={p.isPast ? 'nb-muted' : undefined}>{p.isPast ? 'Paid' : 'Upcoming'}</td>
            {canEdit && (
              <td style={{ textAlign: 'right' }}>
                {/* Past periods offer nothing: an edit would regroup pay that
                    has already been presented. */}
                {p.isPast ? (
                  <span style={{ fontSize: 11, color: 'var(--nb-faint)' }}>Locked</span>
                ) : (
                  <button
                    type="button"
                    className="nb-btn nb-btn-ghost"
                    disabled={busy}
                    onClick={() => onRemove(p.id)}
                  >
                    Remove
                  </button>
                )}
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function AddPeriodForm({
  nextStart,
  busy,
  onAdd,
}: {
  nextStart: string | null;
  busy: boolean;
  onAdd: (input: { startsOn: string; endsOn: string; payDate: string }) => void;
}) {
  const [startsOn, setStartsOn] = useState(nextStart ?? '');
  const [endsOn, setEndsOn] = useState('');
  const [payDate, setPayDate] = useState('');
  const ready = startsOn !== '' && endsOn !== '' && payDate !== '';

  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
      <label style={{ fontSize: 11 }}>
        <span style={{ display: 'block', color: 'var(--nb-subtle)' }}>Starts</span>
        <input
          type="date"
          aria-label="Period start"
          className="nb-input"
          value={startsOn}
          onChange={(e) => setStartsOn(e.target.value)}
        />
      </label>
      <label style={{ fontSize: 11 }}>
        <span style={{ display: 'block', color: 'var(--nb-subtle)' }}>Ends</span>
        <input
          type="date"
          aria-label="Period end"
          className="nb-input"
          value={endsOn}
          onChange={(e) => setEndsOn(e.target.value)}
        />
      </label>
      <label style={{ fontSize: 11 }}>
        <span style={{ display: 'block', color: 'var(--nb-subtle)' }}>Paid on</span>
        <input
          type="date"
          aria-label="Pay date"
          className="nb-input"
          value={payDate}
          onChange={(e) => setPayDate(e.target.value)}
        />
      </label>
      <button
        type="button"
        className="nb-btn nb-btn-primary"
        disabled={busy || !ready}
        onClick={() => {
          onAdd({ startsOn, endsOn, payDate });
          setEndsOn('');
          setPayDate('');
        }}
      >
        Add period
      </button>
    </div>
  );
}

export default function PayPeriodsPage() {
  const qc = useQueryClient();
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission(Permission.UPDATE_SETTINGS);
  const [error, setError] = useState<string | null>(null);

  const q = useQuery({ queryKey: PERIODS_KEY, queryFn: fetchPayPeriods, staleTime: 30_000 });

  const opts = {
    onMutate: () => setError(null),
    onSuccess: () => qc.invalidateQueries({ queryKey: PERIODS_KEY }),
    onError: (err: Error) => setError(err.message),
  };
  const add = useMutation({ mutationFn: createPayPeriod, ...opts });
  const remove = useMutation({ mutationFn: deletePayPeriod, ...opts });

  if (q.isLoading) return <div style={{ fontSize: 12 }}>Loading pay calendar…</div>;
  if (q.isError) {
    return (
      <div role="alert" style={{ fontSize: 12, color: 'var(--nb-warn)' }}>
        {(q.error as Error).message}
      </div>
    );
  }

  const periods = q.data ?? [];
  const span = coveredSpan(periods);
  const busy = add.isPending || remove.isPending;

  return (
    <div style={{ display: 'grid', gap: 14, maxWidth: 760 }}>
      <p style={{ margin: 0, fontSize: 12, color: 'var(--nb-subtle)' }}>
        {span
          ? `Covering ${span}. Periods are contiguous — a new one continues from where the calendar ends.`
          : 'No pay periods yet. The first one you add defines where the calendar begins.'}
      </p>

      {error && (
        <div role="alert" style={{ fontSize: 12, color: 'var(--nb-warn)' }}>
          {error}
        </div>
      )}

      <PeriodTable
        periods={periods}
        canEdit={canEdit}
        busy={busy}
        onRemove={(id) => remove.mutate(id)}
      />

      {canEdit ? (
        <AddPeriodForm
          key={span ?? 'empty'}
          nextStart={nextStartAfter(periods)}
          busy={busy}
          onAdd={(input) => add.mutate(input)}
        />
      ) : (
        <p style={{ margin: 0, fontSize: 11, color: 'var(--nb-faint)' }}>
          Only admins can change the pay calendar.
        </p>
      )}
    </div>
  );
}
